'use client'

import React, { FunctionComponent, useContext, useEffect, useMemo } from 'react'
import { toast, Toaster } from 'sonner'
import { queryOptions, useQuery } from '@tanstack/react-query'
import NotificationsContext from '@/app/_providers/NotificationsProvider/NotificationsContext'
import styles from './NotificationsProvider.module.css'
import { ToasterNotification } from '@/app/_components/Alerts/Notifications'
import { getGlobalNotificationsAction } from '@/app/_actions/notifications.actions'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'
import { QUERY_KEYS } from '@/config/tanstack-querykeys.config'
import TheGreatContext from '@/app/_providers/TheGreatProvider/TheGreatContext'
import { GLOBAL_NOTIFICATIONS_STALE_TIME } from '@/config/tanstack-query-settings.config'

type Props = {
  children: React.ReactNode
}

const NotificationsProvider: FunctionComponent<Props> = ({ children }: Props) => {
  const { incomingQueries: { promoCodes } } = useContext(TheGreatContext)
  const seller = useSeller()

  const notificationsQuery = queryOptions({
    queryKey: [QUERY_KEYS.GLOBAL_NOTIFICATIONS, seller.id, seller.timezone, promoCodes],
    queryFn: () => getGlobalNotificationsAction({
      sellerId: seller.id,
      promoCodes,
      timezone: seller.timezone,
    }),
  })

  const { data: notificationData, error } = useQuery({
    ...notificationsQuery,
    enabled: !!seller.id,
    staleTime: GLOBAL_NOTIFICATIONS_STALE_TIME,
  })

  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching global notifications:', error)
    }
  }, [error])

  useEffect(() => {
    if (notificationData) {
      // Display all toasts now
      notificationData?.Toast?.forEach((notification) => toast.custom((id) => (
        <ToasterNotification notification={notification} onDismiss={() => toast.dismiss(id)} />
      ), { id: notification.id, duration: Infinity }))
    }
  }, [notificationData])

  const value = useMemo(() => ({ notifications: notificationData || null }), [notificationData])

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <Toaster
        className={styles.ToasterContainer}
        position="top-right"
        expand
        visibleToasts={Infinity}
        offset={{ top: 71, right: 16 }}
        mobileOffset={{ top: 51, right: 16, left: 16 }}
      />
    </NotificationsContext.Provider>
  )
}

export default NotificationsProvider
